import { SxProps, Theme } from '@mui/material/styles';
import { pagePadding, rowFlex, flexCenter, centerAlignmentWithSpaceBetween } from '../lib/styles/commonStyles';
import colors from '../lib/styles/colors';

export const rootGridSx = {
  ...pagePadding,
  marginBottom: '32px',
};

export const searchBarSx: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#F5F5F5',
  borderRadius: 1,
  width: '300px',
  '& .MuiInputBase-root': {
    height: '32px',
  },
});

export const breadcrumbStyles: SxProps<Theme> = {
  pl: 4,
  pb: 0,
};

export const scheduledReportsTabsSx: SxProps<Theme> = {
  borderBottom: 1,
  borderColor: 'divider',
  mb: 3,
};

export const scheduledReportCardSx: SxProps<Theme> = (theme: Theme) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? colors.reportCardHover.dark : colors.reportCardHover.light,
  },
  boxShadow: '0px 0px 0px 1px rgba(12, 26, 75, 0.03), 0px 3px 4px 0px rgba(50, 50, 71, 0.05)',
  minHeight: '106px',
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'row',
  cursor: 'pointer',
});

export const cardAction: SxProps<Theme> = (theme: Theme) => ({
  '&.MuiButtonBase-root': {
    '&:focus': {
      border: `2px solid ${theme.palette.secondary.main}`,
      backgroundColor: theme.palette.mode === 'dark' ? colors.reportCardHover.dark : colors.reportCardHover.light,
    },
  },
  '&.Mui-focusVisible .MuiCardActionArea-focusHighlight': {
    opacity: 0,
  },
  '&.MuiCardActionArea-root:hover .MuiCardActionArea-focusHighlight': {
    opacity: 0,
  },
  width: '100%',
});

export const titleBoxSx = {
  ...centerAlignmentWithSpaceBetween,
  height: '24px',
};

export const titleSx = {
  fontSize: '16px',
  fontWeight: 600,
};

export const descriptionSx = {
  fontSize: '16px',
  fontWeight: 400,
};

export const iconContainerSx = {
  ...flexCenter,
};

export const moreOptionsIconButtonSx: SxProps<Theme> = () => ({
  width: '32px',
  height: '32px',
});

export const menuItemSx: SxProps<Theme> = {
  borderRadius: '8px',
  mx: 1,
  my: 0.2,
  px: 2,
  minHeight: 32,
  fontSize: '1rem',
  color: '#161616',
  '&:hover': {
    bgcolor: '#F6F6F6',
    fontWeight: 500,
  },
};

export const menuItemLoadingContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

export const dividerSx = {
  width: '100%',
  margin: 'auto',
  borderColor: 'divider',
  marginTop: '0px',
  marginBottom: '0px',
};

// ScheduleSettingsView styles - Recipient display
export const recipientDisplayContainerSx: SxProps<Theme> = {
  ...rowFlex,
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: 0.5,
};

export const viewMoreRecipientsButtonSx: SxProps<Theme> = {
  textTransform: 'none',
  minWidth: 'auto',
  p: 0,
  minHeight: 'auto',
  fontSize: 'inherit',
  fontWeight: 400,
  lineHeight: 'inherit',
  verticalAlign: 'baseline',
};

// Searchable list popover styles
export const searchableListPopoverPaperSx: SxProps<Theme> = {
  width: '320px',
  maxHeight: '400px',
  borderRadius: 2,
  mt: 0.5,
  boxShadow: 3,
  overflow: 'hidden',
};

export const searchableListSearchFieldSx = {
  mb: 1,
  ml: 0,
  width: '100%',
};

export const searchableListMenuListSx: SxProps<Theme> = {
  maxHeight: '280px',
  overflowY: 'auto',
  p: 0,
};

export const searchableListMenuItemSx: SxProps<Theme> = {
  fontSize: '0.875rem',
  py: 1,
  '&:hover': {
    backgroundColor: 'action.hover',
  },
};

export const searchableListNoResultsBoxSx: SxProps<Theme> = {
  p: 2,
  textAlign: 'center',
};

export const chipContainerSx: SxProps<Theme> = {
  ...rowFlex,
  gap: '8px',
  flexWrap: 'wrap',
  marginBottom: '12px',
};

export const storesChipSx: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.report.subtle,
});

export const recipientsChipSx: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.report.subtleAccent,
});

export const scheduledByBoxSx = {
  marginTop: '4px',
};

export const rowGridSx: SxProps<Theme> = (theme: Theme) => ({
  display: 'grid',
  gap: '16px',
  width: '100%',
  '& > *': {
    maxWidth: 'none',
    boxSizing: 'border-box',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(1, 1fr)',
  },
  [theme.breakpoints.between('md', 'lg')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
});

export const filtersContainerSx: SxProps<Theme> = {
  gap: 2,
  marginBottom: '24px',
};

export const filtersLeftBoxSx: SxProps<Theme> = {
  ...rowFlex,
  gap: 2,
  alignItems: 'center',
  flexWrap: 'wrap',
};

// ScheduledReportsPage styles
export const scheduleReportsPageHeaderSx: SxProps<Theme> = (theme: Theme) => ({
  mt: 2,
  mb: 1,
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,
});

export const scheduleReportButtonContainerSx: SxProps<Theme> = {
  ...flexCenter,
  gap: 1,
};

export const scheduleReportButtonSx: SxProps<Theme> = (theme: Theme) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    backgroundColor: isDarkMode ? theme.palette.primary.dark : theme.palette.primary.main,
    color: theme.palette.getContrastText(isDarkMode ? theme.palette.primary.dark : theme.palette.primary.main),
    '&:hover': {
      backgroundColor: isDarkMode ? theme.palette.primary.dark : theme.palette.primary.main,
    },
  };
};

// ScheduledReportCard styles
export const reportCardInnerBoxSx: SxProps<Theme> = {
  padding: '16px',
};

export const reportCardContentBoxSx: SxProps<Theme> = {
  padding: '0px 16px 16px 16px',
};

export const reportCardInfoRowSx: SxProps<Theme> = {
  ...flexCenter,
  justifyContent: 'left',
  gap: 1,
  marginBottom: '8px',
};

export const reportCardIconSx: SxProps<Theme> = {
  fontSize: '16px',
  color: 'text.secondary',
};

export const reportCardInfoRowBottomSx: SxProps<Theme> = {
  ...flexCenter,
  justifyContent: 'left',
  gap: 1,
  marginBottom: '12px',
};

// ScheduledReportsTabs styles
export const tabPanelBoxSx: SxProps<Theme> = {
  pt: 3,
};

// ScheduledReportDetailsPage styles
export const detailsPageHeaderSx: SxProps<Theme> = (theme: Theme) => ({
  mt: 2,
  mb: 3,
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,
  borderRadius: theme.shape.borderRadius,
});

export const editScheduleButtonSx: SxProps<Theme> = (theme: Theme) => {
  const isDarkMode = theme.palette.mode === 'dark';
  return {
    backgroundColor: isDarkMode ? theme.palette.primary.dark : theme.palette.primary.main,
    color: theme.palette.getContrastText(isDarkMode ? theme.palette.primary.dark : theme.palette.primary.main),
    '&:hover': {
      backgroundColor: isDarkMode ? theme.palette.primary.dark : theme.palette.primary.main,
    },
    textTransform: 'none',
  };
};

export const detailsPageSpacingSx: SxProps<Theme> = {
  mb: 3,
};

// ScheduleSettingsView & ReportSettingsView styles
export const settingsViewContainerSx: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  padding: 3,
  borderRadius: '20px',
};

export const settingsViewTitleSx: SxProps<Theme> = {
  fontSize: '18px',
  fontWeight: '600',
};

export const settingsViewTitleWithMarginSx: SxProps<Theme> = {
  fontSize: '18px',
  fontWeight: '600',
  mb: 2,
};

export const settingsViewFieldLabelSx: SxProps<Theme> = {
  fontWeight: 600,
};

export const settingsViewFieldLabelWithMarginSx: SxProps<Theme> = {
  fontWeight: 600,
  mb: 0.5,
};

export const searchableListHeaderContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 1,
};

export const searchableListPopoverHeaderSx: SxProps<Theme> = {
  fontWeight: 600,
  flex: 1,
};

export const searchableListCloseButtonSx: SxProps<Theme> = {
  ml: 1,
};

export const settingsViewFieldValueSx: SxProps<Theme> = {
  wordBreak: 'break-word',
};

export const settingsViewSecuritySwitchBoxSx: SxProps<Theme> = {
  ...flexCenter,
  justifyContent: 'left',
  gap: 2,
};

export const reportSettingsFilterChipBoxSx: SxProps<Theme> = {
  ...rowFlex,
  flexWrap: 'wrap',
  gap: 0.5,
};

export const reportSettingsDividerSx: SxProps<Theme> = {
  borderTop: '1px solid',
  borderColor: 'divider',
  mb: 2,
};

export const securitySwitchFormLabelSx: SxProps<Theme> = {
  m: 0,
};

export const notFoundContainerSx: SxProps<Theme> = {
  mt: 4,
};

export const noSettingsDescriptionSx: SxProps<Theme> = {
  pt: 2,
};

export const tabsContainerSx: SxProps<Theme> = {
  width: '100%',
};
